/**
 * Dragging on the slide (#deck-machine, 2026-10-16).
 *
 * - SELECT: press on empty space (or on a page-filling background) and drag: a box selects everything it touches,
 *   like Canva or PowerPoint. Shift adds to the selection. A press on an element still moves it; a press in text still
 *   places the caret.
 * - MARK: "Ask Lykke" on the slide, then drag over the part she should change. The mark travels with the next chat
 *   message: the page, the area (percent of the page), the elements inside and the words it covers on a designed page.
 *
 * Listens on the window in the capture phase, so it runs before the elements' own press handlers (a background
 * picture must not start moving when the drag is meant to select).
 */
import { useEffect, useRef, useState, type RefObject } from 'react';

export interface MarkRect { x: number; y: number; w: number; h: number }
export interface DeckMark { index: number; /** The page's own id: the mark follows it when pages move, and goes when it is deleted. */ pageId?: string; region?: MarkRect; elementIds?: string[]; texts?: string[]; label: string }

const CONTROLS = 'button,input,select,textarea,a,[data-ebox-handle],[data-ebox-tools],[data-emedia-tools],[data-emedia-pos],[data-stage-ui]';
const round = (n: number) => Math.round(n * 10) / 10;

type Box = { id: string; kind?: string; x?: number; y?: number; w?: number; h?: number };
/** A page's elements with the ids the editor gives them (legacy text boxes without one are `legacy-i`, as drawn). */
export function stageElements(slide: unknown): Box[] {
  const s = slide as { boxes?: unknown[]; elements?: unknown[] } | null;
  const legacy = (Array.isArray(s?.boxes) ? s!.boxes : []).map((b, i) => ({ ...(b as object), id: String((b as Box)?.id || `legacy-${i}`) }) as Box);
  const els = (Array.isArray(s?.elements) ? s!.elements : []).filter((e) => e && typeof e === 'object' && (e as { kind?: string }).kind) as Box[];
  return [...legacy, ...els];
}

/** Covers most of the page: an imported deck's background picture or panel (a page-size text frame is still text). */
export const isBackgroundBox = (e: { kind?: string; w?: number; h?: number }) => (e.kind === 'image' || e.kind === 'shape') && (Number(e.w) || 0) >= 90 && (Number(e.h) || 0) >= 85;

/** Elements a box touches (a background only when the box holds all of it). Ids as the editor shows them. */
export function elementsIn(slide: unknown, r: MarkRect): string[] {
  return stageElements(slide).filter((e) => {
    const x = Number(e.x) || 0, y = Number(e.y) || 0, w = Number(e.w) || 0, h = Number(e.h) || 0;
    if (isBackgroundBox(e)) return r.x <= x + 0.5 && r.y <= y + 0.5 && r.x + r.w >= x + w - 0.5 && r.y + r.h >= y + h - 0.5;
    return x < r.x + r.w && x + w > r.x && y < r.y + r.h && y + h > r.y;
  }).map((e) => e.id);
}

/** Where on the page, in words. */
export function placeLabel(r: MarkRect): string {
  const cx = r.x + r.w / 2, cy = r.y + r.h / 2;
  if (r.w >= 80 && r.h >= 80) return 'most of the page';
  const v = cy < 34 ? 'top' : cy > 66 ? 'bottom' : 'middle';
  const h = cx < 34 ? 'left' : cx > 66 ? 'right' : 'centre';
  return v === 'middle' && h === 'centre' ? 'centre' : v === 'middle' ? `${h} side` : `${v} ${h}`;
}

/** The words a box covers on the rendered page, html pages (shadow DOM) included: text blocks at least half inside. */
export function textsIn(stage: HTMLElement, px: DOMRect): string[] {
  const out: string[] = [];
  const visit = (root: Element | ShadowRoot) => {
    for (const el of Array.from(root.querySelectorAll('*'))) {
      if ((el as HTMLElement).shadowRoot) visit((el as HTMLElement).shadowRoot!);
      if (el.closest(CONTROLS)) continue; // the editor's own tools (an image's Position / Replace), not the page
      const own = Array.from(el.childNodes).some((n) => n.nodeType === 3 && (n.textContent || '').trim().length > 1);
      if (!own) continue;
      const b = el.getBoundingClientRect(); const area = b.width * b.height; if (!area) continue;
      const ix = Math.max(0, Math.min(b.right, px.right) - Math.max(b.left, px.left)); const iy = Math.max(0, Math.min(b.bottom, px.bottom) - Math.max(b.top, px.top));
      if ((ix * iy) / area < 0.5) continue;
      const t = ((el as HTMLElement).innerText || el.textContent || '').replace(/\s+/g, ' ').trim();
      if (t.length > 1) out.push(t.slice(0, 300));
    }
  };
  visit(stage);
  // A block whose words another picked block already holds adds nothing.
  const uniq = [...new Set(out)];
  return uniq.filter((t) => !uniq.some((o) => o !== t && o.includes(t))).slice(0, 20);
}

/** A mark from a box on the page (or from the selected elements' bounds). */
export function markFrom(stage: HTMLElement | null, slide: unknown, index: number, r: MarkRect, ids?: string[]): DeckMark {
  const elementIds = ids && ids.length ? ids : elementsIn(slide, r);
  let texts: string[] = [];
  if (stage) { const s = stage.getBoundingClientRect(); texts = textsIn(stage, new DOMRect(s.left + (r.x / 100) * s.width, s.top + (r.y / 100) * s.height, (r.w / 100) * s.width, (r.h / 100) * s.height)); }
  const pageId = (slide as { _id?: unknown } | null)?._id;
  return { index, ...(typeof pageId === 'string' ? { pageId } : {}), region: { x: round(r.x), y: round(r.y), w: round(r.w), h: round(r.h) }, ...(elementIds.length ? { elementIds } : {}), ...(texts.length ? { texts } : {}), label: `Page ${index + 1} · ${placeLabel(r)}` };
}

/** The bounds of elements (percent of the page). */
export function boundsOf(slide: unknown, ids: string[]): MarkRect | null {
  const els = stageElements(slide).filter((e) => ids.includes(e.id)); if (!els.length) return null;
  const x0 = Math.min(...els.map((e) => Number(e.x) || 0)), y0 = Math.min(...els.map((e) => Number(e.y) || 0));
  const x1 = Math.max(...els.map((e) => (Number(e.x) || 0) + (Number(e.w) || 0))), y1 = Math.max(...els.map((e) => (Number(e.y) || 0) + (Number(e.h) || 0)));
  return { x: Math.max(0, x0), y: Math.max(0, y0), w: Math.min(100, x1) - Math.max(0, x0), h: Math.min(100, y1) - Math.max(0, y0) };
}

/** Drag on the stage: select (a box) or mark (an area for Lykke). Returns the box being drawn, in page percent. */
export function useStageDrag(stageRef: RefObject<HTMLElement | null>, o: { enabled: boolean; mode: 'select' | 'mark'; slide: unknown; onSelect: (ids: string[], add: boolean) => void; onMark: (r: MarkRect) => void }): MarkRect | null {
  const [box, setBox] = useState<MarkRect | null>(null);
  const opts = useRef(o); opts.current = o;
  useEffect(() => {
    if (!o.enabled) return;
    const down = (e: PointerEvent) => {
      const stage = stageRef.current; if (!stage || e.button !== 0) return;
      const path = e.composedPath(); if (!path.includes(stage)) return;
      const t0 = path[0] as Element; const target = e.target as HTMLElement | null;
      const { mode } = opts.current;
      if (mode === 'select') {
        if (!target || target.closest(CONTROLS)) return;
        // Text is for typing (and its own drag selects words); a designed page handles its own presses.
        if ((t0 as HTMLElement).isContentEditable || target.isContentEditable || target.closest('[contenteditable="true"]') || (t0?.getRootNode?.() instanceof ShadowRoot)) return;
        const eid = (target.closest('[data-eid]') as HTMLElement | null)?.dataset.eid;
        if (eid) { const el = stageElements(opts.current.slide).find((x) => x.id === eid); if (!el || !isBackgroundBox(el)) return; }
        // A background is never moved by a drag meant to select; typing elsewhere ends, as a click on the page would.
        if (eid) { e.preventDefault(); e.stopPropagation(); (document.activeElement as HTMLElement | null)?.blur?.(); }
        start(e, stage, eid);
      } else {
        if (target?.closest('[data-stage-ui]')) return;
        e.preventDefault(); e.stopPropagation(); start(e, stage);
      }
    };
    const start = (e: PointerEvent, stage: HTMLElement, pressedBackground?: string) => {
      const s = stage.getBoundingClientRect(); const sx = e.clientX, sy = e.clientY; const add = e.shiftKey; let moved = false;
      const pct = (cx: number, cy: number): MarkRect => {
        const x0 = Math.max(0, Math.min(sx, cx) - s.left), y0 = Math.max(0, Math.min(sy, cy) - s.top);
        const x1 = Math.min(s.width, Math.max(sx, cx) - s.left), y1 = Math.min(s.height, Math.max(sy, cy) - s.top);
        return { x: (x0 / s.width) * 100, y: (y0 / s.height) * 100, w: (Math.max(0, x1 - x0) / s.width) * 100, h: (Math.max(0, y1 - y0) / s.height) * 100 };
      };
      const move = (ev: PointerEvent) => {
        if (!moved && Math.hypot(ev.clientX - sx, ev.clientY - sy) < 5) return;
        if (!moved) { moved = true; window.getSelection()?.removeAllRanges(); }
        ev.preventDefault(); setBox(pct(ev.clientX, ev.clientY));
      };
      const up = (ev: PointerEvent) => {
        window.removeEventListener('pointermove', move, true); window.removeEventListener('pointerup', up, true); window.removeEventListener('pointercancel', up, true);
        setBox(null);
        if (ev.type !== 'pointerup') return;
        if (!moved) { if (pressedBackground && opts.current.mode === 'select') opts.current.onSelect([pressedBackground], add); return; }
        // The click after a drag must not select or place a caret under the pointer.
        const swallow = (c: Event) => { c.stopPropagation(); c.preventDefault(); };
        window.addEventListener('click', swallow, { capture: true, once: true }); setTimeout(() => window.removeEventListener('click', swallow, { capture: true }), 0);
        const r = pct(ev.clientX, ev.clientY);
        if (opts.current.mode === 'mark') { if (r.w >= 1.5 && r.h >= 1.5) opts.current.onMark(r); return; }
        opts.current.onSelect(elementsIn(opts.current.slide, r), add);
      };
      window.addEventListener('pointermove', move, true); window.addEventListener('pointerup', up, true); window.addEventListener('pointercancel', up, true);
    };
    window.addEventListener('pointerdown', down, true);
    return () => window.removeEventListener('pointerdown', down, true);
  }, [o.enabled, stageRef]);
  return box;
}
